import { Button, message, Modal } from 'antd';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import Image from 'next/image';
import { FakeSearchModel } from '@/types/home';
import router from 'next/router';
import { getPersionalProductApi, postAccountAddApi, postAddManualClewApi } from '@/lib/service';
import { useUserProvider } from '@/context/UserProvider';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

const AccountModal = ({
  currentModel,
  closeAction,
}: {
  currentModel?: FakeSearchModel;
  closeAction?: (isOk: boolean) => void;
}) => {
  const { isLogin, onOpenLoginModal } = useUserProvider();
  const { t } = useTranslation();
  const [selectedAccounts, setSelectedAccounts] = useState<Set<any>>(new Set());
  const [loading, setLoading] = useState(false);

  const handleSelect = (item: any) => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(item)) {
      newSelected.delete(item);
    } else {
      newSelected.add(item);
    }
    setSelectedAccounts(newSelected);
  };

  const handleRemove = () => {
    if (!isLogin) {
      onOpenLoginModal(true);
      return;
    }

    if (loading) {
      return;
    }
    setLoading(true);
    ga_logEvent(EventName.click_remove_now);

    getPersionalProductApi()
      .then((res) => {
        if (res?.code === 0) {
          const tempModel = res?.data?.at(0);

          if (!tempModel || tempModel?.item_id === 1 || tempModel?.item_id === 2) {
            const fakeAccounts = Array.from(selectedAccounts).map((item) => `fakes_${item?.id}`);
            const keyValue = `id_${currentModel?.key},account_${currentModel?.user_account_info
              ?.id},${fakeAccounts.join(',')}`;
            router.push(`/plan?key=${keyValue}`);
            setLoading(false);
            return;
          } else {
            postAccountAddApi({
              url: currentModel?.user_account_info?.url,
              name: currentModel?.user_account_info?.name,
              username: currentModel?.user_account_info?.username,
              avatar: currentModel?.user_account_info?.avatar_url,
              platform: currentModel?.user_account_info?.social_type,
              description: currentModel?.user_account_info?.profile_description,
              verify_status: 2,
            })
              .then((rs) => {
                if (rs.code == 0) {
                  const fakeAccounts = Array.from(selectedAccounts).map((item) => `${item?.url}`);
                  postAddManualClewApi({ links: fakeAccounts })
                    .then((res) => {
                      if (res.code == 0) {
                        router.push(`/leaks-found`);
                      } else {
                        message.error(res?.message);
                      }
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                } else {
                  message.warning(rs.message);
                  setLoading(false);
                }
              })
              .catch((error) => {
                message.error(error?.message);
                setLoading(false);
              });
          }
        } else {
          message?.error(res?.message);
          setLoading(false);
        }
      })
      .catch((error) => {
        message?.error(error?.message);
        setLoading(false);
      });
  };

  return (
    <Modal
      width={450}
      open={currentModel !== undefined && currentModel !== null}
      onCancel={() => {
        if (closeAction) {
          closeAction(false);
        }
      }}
      footer={null}
      destroyOnClose={true}
    >
      <div className={styles.impersonationDetection}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('Impersonation Detection')}</h2>
        </div>
        <div className={styles.content}>
          <p className={styles.text}>
            {t('Your account')} <span className={styles.highlight}>@{currentModel?.user_account_info?.username}</span>,{' '}
            {t('please confirm if this is your account.')}
          </p>
          <div className={styles.userInfo}>
            <Image
              src={currentModel?.user_account_info?.avatar_url ?? '/images/erasa/platforms/space.png'}
              alt="User avatar"
              width={40}
              height={40}
              className={styles.avatar}
            />
            <div className={styles.userDetails}>
              <div className={styles.username}>{currentModel?.user_account_info?.username}</div>
            </div>
          </div>
          <p className={styles.text}>
            <span style={{ color: '#428DFF' }}>{currentModel?.similar_users?.length}</span>{' '}
            {t('suspected counterfeit accounts. Select accounts for Erasa to remove.')}
          </p>
          <div className={styles.accountList}>
            {currentModel?.similar_users?.map((account) => (
              <label key={account?.username} className={styles.accountItem}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedAccounts.has(account)}
                  onChange={() => handleSelect(account)}
                />
                <div className={styles.accountDetails}>
                  <Image
                    src={account?.avatar_url ?? '/images/erasa/platforms/space.png'}
                    alt={`${account?.username}'s avatar`}
                    width={40}
                    height={40}
                    className={styles.avatar}
                  />
                  <div className={styles.userDetails}>
                    <div className={styles.username}>{account?.username}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.removeButton}
            disabled={selectedAccounts.size === 0}
            onClick={handleRemove}
            loading={loading}
          >
            {t('Remove Now')} ({selectedAccounts.size})
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AccountModal;
